import { getIOSPushStatus, getPermissionStatus, isPushSupported } from '@/utils/pushNotifications'

export function isNotificationPromptShown(store) {
  return store.state.notificationPromptShown
}

export function markNotificationPromptShown(store) {
  store.commit('SET_NOTIFICATION_PROMPT_SHOWN', true)
}

export function clearNotificationPromptShown(store) {
  store.commit('SET_NOTIFICATION_PROMPT_SHOWN', false)
}

export function markIOSInstallPromptDismissed(store) {
  store.commit('SET_IOS_INSTALL_PROMPT_DISMISSED', true)
}

function isIOSInstallPromptDismissed(store) {
  return store.state.iosInstallPromptDismissed
}

/**
 * Determine whether the notification prompt should be shown.
 *
 * Rules (P1): the prompt is only shown when it is actually needed:
 * - already declined via "Not now" (`notificationPromptShown`) → no
 * - account-level push disabled (`pushNotificationsEnabled === false`) → no
 * - iOS too old / iOS tab already dismissed → no
 * - permission `granted` → no (subscription is kept alive silently on launch)
 * - permission `denied` → no on auto-show; only an explicit `force`
 *   (profile checkbox / gate) may show the "blocked" notice
 *
 * @param {object} api - API client instance
 * @param {object} store - Vuex store instance
 * @param {object} [opts] - Options
 * @param {boolean} [opts.force] - Force show regardless of shown state
 * @returns {Promise<boolean>}
 */
export async function shouldShowNotificationPrompt(api, store, { force = false } = {}) {
  if (!force && isNotificationPromptShown(store)) {
    return false
  }

  if (!force && store.state.user?.user?.pushNotificationsEnabled === false) {
    return false
  }

  const iosPushStatus = getIOSPushStatus()

  if (iosPushStatus === 'too-old') {
    return false
  }

  if (!force && iosPushStatus === 'not-standalone' && isIOSInstallPromptDismissed(store)) {
    return false
  }

  // iOS Safari/Chrome tab does not expose `window.PushManager` (Push API is
  // only available in the installed Home Screen PWA, iOS 16.4+). Do not treat
  // that as "unsupported" — the prompt should still open and lead to the iOS
  // coach ("Add to Home Screen") instead of a misleading unsupported notice.
  if (!force && !isPushSupported() && iosPushStatus !== 'not-standalone') {
    return false
  }

  const permission = getPermissionStatus()

  // Granted — never prompt again; the subscription is (re)created silently on launch
  if (permission === 'granted') {
    return false
  }

  // Denied — never prompt on auto-show; only an explicit force may show the notice
  if (permission === 'denied' && !force) {
    return false
  }

  return true
}
